/**
 * 과제 ID 생성 유틸리티
 * 모든 과제 타입에 대해 일관된 ID 형식 제공
 */

export type AssignmentType = 'general' | 'project' | 'routine';

/**
 * 과제 타입별 ID 생성
 * @param type 과제 타입
 * @returns 생성된 ID (예: general_1758003340450, project_1758003340450, routine_1758003340450)
 */
export function generateAssignmentId(type: AssignmentType): string {
  const timestamp = Date.now();
  return `${type}_${timestamp}`;
}

/**
 * 하위 과제 ID 생성 (프로젝트 하위과제용) - 기존 형태 유지
 * @returns 생성된 ID (예: subtask-1758003340450)
 */
export function generateSubTaskId(): string {
  return `subtask-${Date.now()}`;
}

/**
 * 루틴 과제 날짜 ID 생성 - datetask 접두사 + 날짜 문자열
 * @param date 날짜
 * @returns 생성된 ID (예: "datetask-2025-09-19")
 */
export function generateRoutineDateId(date: Date): string {
  const dateStr = date.getFullYear() + '-' + 
    String(date.getMonth() + 1).padStart(2, '0') + '-' + 
    String(date.getDate()).padStart(2, '0');
  return `datetask-${dateStr}`;
}

/**
 * 기존 ID를 새로운 형식으로 변환
 * @param oldId 기존 ID
 * @param type 과제 타입
 * @returns 변환된 ID
 */
export function convertToNewIdFormat(oldId: string, type: AssignmentType): string {
  // 이미 새로운 형식인 경우 그대로 반환
  if (oldId.startsWith(`${type}_`)) {
    return oldId;
  }
  
  // 기존 형식을 새로운 형식으로 변환
  const timestamp = Date.now();
  return `${type}_${timestamp}`;
}

/**
 * ID에서 과제 타입 추출
 * @param id 과제 ID
 * @returns 과제 타입 또는 null
 */
export function extractAssignmentType(id: string): AssignmentType | null {
  if (id.startsWith('general_')) return 'general';
  if (id.startsWith('project_')) return 'project';
  if (id.startsWith('routine_')) return 'routine';
  return null;
}

/**
 * ID가 새로운 형식인지 확인
 * @param id 과제 ID
 * @returns 새로운 형식 여부
 */
export function isNewIdFormat(id: string): boolean {
  return /^(general|project|routine)_\d+$/.test(id);
}

/**
 * 기존 날짜 ID를 새로운 datetask 형식으로 변환
 * @param oldDateId 기존 날짜 ID (예: "2025-09-19")
 * @returns 새로운 형식 ID (예: "datetask-2025-09-19")
 */
export function convertDateIdToNewFormat(oldDateId: string): string {
  // 이미 datetask- 접두사가 있으면 그대로 반환
  if (oldDateId.startsWith('datetask-')) {
    return oldDateId;
  }
  
  // YYYY-MM-DD 형식이면 datetask- 접두사 추가
  if (/^\d{4}-\d{2}-\d{2}$/.test(oldDateId)) {
    return `datetask-${oldDateId}`;
  }
  
  // 다른 형식이면 그대로 반환
  return oldDateId;
}
